import React, { useEffect, useState } from "react";
import {
  Flex,
  TextInput,
  Card,
  Callout,
  Button,
  Divider,
  List,
  ListItem,
  Select,
  SelectItem,
  Switch,
  Badge,
  Text,
} from "@tremor/react";
import { TrashIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import { BarChart } from "@tremor/react";
import Credentials from "../credentials";
import AttributesForm from "./attributes";
import DeleteConfirm from "../../../components/DeleteConfirm";
import * as devicesAPI from "../../../services/device";
import * as profilesAPI from "../../../services/profiles";
import * as userAPI from "../../../services/user";
import Alert from "../../../components/alert";
import Toast from "../../../components/Toast";

import ReactGA from "react-ga4";
import { GA_EVENTS } from "../../../services/ga-events";
import { initAnalytics } from "../../../services/analytics";
 
initAnalytics();


export default function SettingsForm({ device, onUpdate }) {
  const [name, setName] = useState(device.name);
  const [enabled, setEnabled] = useState(device.enabled !== false);
  const [disabled, setDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});
  const [usage, setUsage] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  const [profiles, setProfiles] = useState([]);
  const [selectedProfileId, setSelectedProfileId] = useState(device.profile?._id || "");
  const [subscription, setSubscription] = useState(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  useEffect(() => {
    ReactGA.event({
      category: "Device Settings",
      action: GA_EVENTS.DEVICE_SETTINGS_VIEW,
    });
    setName(device.name);
    setEnabled(device.enabled !== false);
    setSelectedProfileId(device.profile?._id || "");
    
    // Fetch available profiles if device is lora type
    if (device.type === "lora") {
      const fetchProfiles = async () => {
        try {
          const response = await profilesAPI.list(1, 100);
          setProfiles(response.data || []);
        } catch (err) {
          console.error("Error fetching profiles:", err);
        }
      };
      fetchProfiles();
    }
    
    // get usage data for device
    const fetchUsage = async () => {
      try {
        const data = await devicesAPI.getDeviceUsage(device._id);
        
        let total = 0;

        // we want to show the last 24 hours of data and backfill with 0s
        const now = new Date();
        now.setMinutes(0);
        const last24Hours = new Date(now - 24 * 60 * 60 * 1000);
        const last24HoursData = data.filter(
          (item) => new Date(item._id.timestamp) > last24Hours,
        );

        // Create an array of the last 24 hours
        const hours = [];
        for (let i = 0; i < 24; i++) {
          const hour = new Date(now - i * 60 * 60 * 1000);
          hours.unshift(hour);
        }

        // Format data for tremor bar chart and backfill with 0s
        const bData = hours.map((hour) => {
          const item = last24HoursData.find((dataItem) => {
            const dataHour = new Date(dataItem._id.timestamp);
            return (
              dataHour.getHours() === hour.getHours() &&
              dataHour.getDate() === hour.getDate()
            );
          });

          const messages = item ? item.totalUplinkCount : 0;
          total += messages;

          return {
            datetime: hour.toLocaleTimeString("en-US", {
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            }),
            Messages: messages,
          };
        });
        setUsage(bData);
        setTotalCount(total);
      } catch (err) {
        console.error("Error fetching usage data:", err);
      }
    };

    // get subscription data for account limits
    const fetchSubscription = async () => {
      try {
        const data = await userAPI.getSubscription();
        setSubscription(data);
      } catch (err) {
        console.error("Error fetching subscription data:", err);
      }
    };

    fetchUsage();
    fetchSubscription();
  }, [device]);

  const onDeleteConfirm = async () => {
    try {
      setDisabled(true);
      await devicesAPI.removeDevice(device._id);
      navigate("/devices");
    } catch (err) {
      console.error("Error deleting device:", err);
      setDisabled(false);
      
      // Show error toast for device deletion
      let errorMessage = "Failed to delete device";
      if (err.response) {
        errorMessage = err.response.data?.message || 
                      (err.response.status === 401 ? "Unauthorized access" : 
                       err.response.status === 403 ? "You don't have permission to delete this device" : 
                       "Failed to delete device");
      }
      
      setToast({
        show: true,
        message: errorMessage,
        type: "error"
      });
    }
  };

  const validateForm = () => {
    let errors = {};

    if (!name) errors.name = "Name is required";
    setErrors(errors);
    setDisabled(Object.keys(errors).length > 0);
    setTimeout(() => setErrors({}), 5000);
    console.log(errors);
    return Object.keys(errors).length > 0;
  };

  const onSave = async (e) => {
    e.preventDefault();
    try {
      if (validateForm()) return;

      const updateData = { name, enabled };
      
      // Only include profile if it has changed and device is lora type
      if (device.type === "lora" && selectedProfileId !== (device.profile?._id || "")) {
        updateData.profile = selectedProfileId || null;
      }
      setIsLoading(true);
      await devicesAPI.patchDevice(device._id, updateData);
      
      // Update local device object
      device.name = name;
      device.enabled = enabled;
      if (updateData.profile !== undefined) {
        // Find the selected profile object from profiles array
        const selectedProfile = profiles.find(p => p._id === selectedProfileId);
        device.profile = selectedProfile || null;
      }
      setIsLoading(false);
      setToast({
        show: true,
        message: "Device settings saved successfully",
        type: "success"
      });
      onUpdate(device);
    } catch (err) {
      console.error("Error updating device:", err);
      setDisabled(false);
      setIsLoading(false);
      
      // Show error toast with appropriate message
      let errorMessage = "Failed to update device settings";
      if (err.response) {
        // If we have a response with error details
        errorMessage = err.response.data?.message || 
                      (err.response.status === 400 ? "Invalid request data" : 
                       err.response.status === 401 ? "Unauthorized access" : 
                       err.response.status === 403 ? "You don't have permission to update this device" : 
                       "Failed to update device settings");
      }
      
      setToast({
        show: true,
        message: errorMessage,
        type: "error"
      });
    }
  };

  const onSetName = (e) => {
    setName(e.target.value);
    setErrors({});
    setDisabled(false);
  };
  
  const onProfileChange = (value) => {
    setSelectedProfileId(value);
    setErrors({});
    setDisabled(false);
  };

  return (
    <div>
      {toast.show && (
        <Toast 
          message={toast.message}
          type={toast.type}
          duration={3000}
          onClose={() => setToast({...toast, show: false})}
        />
      )}
      
      <DeleteConfirm
        isOpen={deleteOpen}
        onConfirm={onDeleteConfirm}
        closeModal={() => setDeleteOpen(false)}
        message="Are you sure you want to delete this device?"
      />

      <Divider />
      <section aria-labelledby="general-device-info">
        <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:gap-x-8 md:gap-x-14 md:grid-cols-3">
          <div className="px-4 sm:px-0">
            <h2 className="scroll-mt-10 font-semibold text-gray-900 dark:text-gray-50 text-base sm:text-lg">
              General
            </h2>
            <p className="mt-1 text-xs sm:text-sm leading-5 sm:leading-6 text-gray-500 dark:text-gray-400">
              Device basic info.
            </p>
          </div>
          <div className="space-y-6 md:col-span-2 px-4 sm:px-0">
            <div>
              <Card>
                {Object.keys(errors).length > 0 && (
                  <Alert
                    title="Error"
                    message="Please correct the errors below:"
                  >
                    {Object.values(errors).map((error, index) => (
                      <div key={index}>{error}</div>
                    ))}
                  </Alert>
                )}
                <form>
                  <div className="grid gap-4 mb-4">
                    <div>
                      <label
                        htmlFor="name"
                        className="block mb-2 text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-50"
                      >
                        Name
                      </label>
                      <TextInput
                        id="name"
                        value={name}
                        placeholder="Humidor"
                        onChange={onSetName}
                      />
                    </div>
                  <Divider />
                    <div>
                      <Flex className="gap-4 items-center">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <label
                              htmlFor="enabled"
                              className="text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-50"
                            >
                              Device Status
                            </label>
                            <Badge 
                              color={enabled ? "green" : "red"} 
                              size="xs"
                            >
                              {enabled ? "Enabled" : "Disabled"}
                            </Badge>
                          </div>
                          <Text className="text-xs text-gray-500 dark:text-gray-400">
                            {enabled 
                              ? "Device is enabled and will process data and alerts" 
                              : "Device is disabled and will not process any data or alerts"
                            }
                          </Text>
                        </div>
                        <Switch
                          id="enabled"
                          checked={enabled}
                          onChange={setEnabled}
                        />
                      </Flex>
                    </div>
                    
                    {device.type === "lora" && (
                      <div>
                        <label
                          htmlFor="profile"
                          className="block mb-2 text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-50"
                        >
                          Device Profile
                        </label>
                        <Select
                          id="profile"
                          value={selectedProfileId}
                          onValueChange={onProfileChange}
                          placeholder="Select a profile"
                        >
                          <SelectItem value="">None</SelectItem>
                          {profiles.map((profile) => (
                            <SelectItem key={profile._id} value={profile._id}>
                              {profile.name}
                            </SelectItem>
                          ))}
                        </Select>
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          Select a device profile for this LoRa device
                        </p>
                      </div>
                    )}
                  </div>
                  <Flex justifyContent={"end"}>
                    <Button onClick={onSave} disabled={disabled}>
                      Save
                    </Button>
                  </Flex>
                </form>
              </Card>
            </div>
          </div>
        </div>
      </section>
      <Divider />
      <section aria-labelledby="device-attributes">
        <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:gap-x-8 md:gap-x-14 md:grid-cols-3">
          <div className="px-4 sm:px-0">
            <h2 id="device-attributes" className="scroll-mt-10 font-semibold text-gray-900 dark:text-gray-50 text-base sm:text-lg">
              Attributes
            </h2>
            <p className="mt-1 text-xs sm:text-sm leading-5 sm:leading-6 text-gray-500 dark:text-gray-400">
              Custom key/value metadata for this device. Values are typed so integrations receive real numbers, booleans and dates.
            </p>
          </div>
          <div className="space-y-6 md:col-span-2 px-4 sm:px-0">
            <AttributesForm device={device} onUpdate={onUpdate} />
          </div>
        </div>
      </section>
      <Divider />
      <section aria-labelledby="billing-usage">
        <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:gap-x-8 md:gap-x-14 md:grid-cols-3">
          <div className="px-4 sm:px-0">
            <h2 className="scroll-mt-10 font-semibold text-gray-900 dark:text-gray-50 text-base sm:text-lg">Usage</h2>
            <p className="mt-1 text-xs sm:text-sm leading-5 sm:leading-6 text-gray-500 dark:text-gray-400">
              Device to Cloud events for the last 24 hours.
            </p>
          </div>
          <div className="space-y-6 md:col-span-2 px-4 sm:px-0">
            <h5 className="text-base sm:text-lg font-medium text-tremor-content-strong dark:text-dark-tremor-content-strong">
              Device messages for this Month
            </h5>
            <List>
              <ListItem>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  Total messages in the last 24 hours
                </p>
                <p className="text-xl font-semibold">{totalCount}</p>
              </ListItem>
              {subscription && (
                <>
                  <ListItem>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      Account Plan
                    </p>
                    <div className="flex items-center gap-2">
                      <Badge color={subscription.tier === 'free' ? 'gray' : 'blue'} size="sm">
                        {subscription.tier.charAt(0).toUpperCase() + subscription.tier.slice(1)}
                      </Badge>
                      {subscription.overLimit && (
                        <Badge color="red" size="sm">
                          Over Limit
                        </Badge>
                      )}
                    </div>
                  </ListItem>
                  <ListItem>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      Monthly Event Limit
                    </p>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-50">
                      {subscription.totalEventLimit.toLocaleString()} messages
                    </p>
                  </ListItem>
                  <ListItem>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      Hourly Event Limit
                    </p>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-50">
                      {subscription.hourlyEventLimit.toLocaleString()} messages/hour
                    </p>
                  </ListItem>
                  <ListItem>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      Device Limit
                    </p>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-50">
                      {subscription.deviceLimit} devices
                    </p>
                  </ListItem>
                  <ListItem>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      Data Retention
                    </p>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-50">
                      {subscription.dataRetention} {subscription.dataRetention === 1 ? 'day' : 'days'}
                    </p>
                  </ListItem>
                </>
              )}
            </List>
            <Card>
              <BarChart
                className="mt-6"
                data={usage}
                index="datetime"
                categories={["Messages"]}
                colors={["blue"]}
                yAxisWidth={48}
              />
            </Card>
          </div>
        </div>
      </section>
      <Divider />
      <section>
        <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:gap-x-8 md:gap-x-14 md:grid-cols-3">
          <div className="px-4 sm:px-0">
            <h2 className="scroll-mt-10 font-semibold text-gray-900 dark:text-gray-50 text-base sm:text-lg">
              MQTT Credentials
            </h2>
            <p className="mt-1 text-xs sm:text-sm leading-5 sm:leading-6 text-gray-500 dark:text-gray-400">
              Device MQTT credentials.
            </p>
          </div>
          <div className="space-y-6 md:col-span-2 px-4 sm:px-0">
            <Card>
              <Credentials clientId={device.serial} />
            </Card>
          </div>
        </div>
      </section>
      <Divider />
      <section>
        <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:gap-x-8 md:gap-x-14 md:grid-cols-3">
          <div className="px-4 sm:px-0">
            <h2 className="scroll-mt-10 font-semibold text-gray-900 dark:text-gray-50 text-base sm:text-lg">
              Danger Zone
            </h2>
          </div>
          <div className="space-y-6 md:col-span-2 px-4 sm:px-0">
            <Card>
              <Callout className="mt-4" color="rose">
                <button
                  onClick={() => setDeleteOpen(true)}
                  disabled={disabled}
                  type="button"
                  className="focus:outline-none inline-flex text-white bg-red-600 hover:bg-red-800 focus:ring-4 focus:ring-red-300 font-medium rounded-lg text-xs sm:text-sm px-4 sm:px-5 py-2 sm:py-2.5 mr-2 mb-2"
                >
                  <TrashIcon className="w-5 h-5 mr-2" />
                  Remove Device
                </button>
              </Callout>
            </Card>
          </div>
        </div>
      </section>
      <Divider />
    </div>
  );
}
